import gql from 'graphql-tag';
import { computed, ref } from 'vue';
import { useQuery } from '@vue/apollo-composable';
import { useAccountInfo } from '@models/account/useAccountInfo';

const QUERY_ALL_ONLINE_PACKAGE_SCHEMA = gql`
    query getAllOnlinePackage($area: String) {
        getAllOnlinePackage(area: $area) {
            pti {
                packageId
                packageName
                alias
                isOnline
                area
                originalPrice {
                    packageId
                    period
                    monthlyOriginalPrice
                    buyMode
                    area
                    taxRate
                }
                level
                currency
                product
                type
                privilege {
                    monthlyPV
                    monthlyEvents
                    collaborateUsers
                    conversions
                    useCrossDomain
                    heatmaps
                    useSegmentation
                    useHeatmapCompetitor
                }
            }
            ptx {
                packageId
                packageName
                alias
                isOnline
                area
                originalPrice {
                    period
                    monthlyOriginalPrice
                    buyMode
                    taxRate
                }
                level
                currency
                product
                type
                privilege {
                    monthlyPV
                    monthlyEvents
                    collaborateUsers
                    conversions
                    useCrossDomain
                    campaigns
                    monthlyCampaignViewUsers
                    userGroups
                    useCampaignAbTest
                }
            }
        }
    }
`;

type PtxAllPrivilegeType = {
    privilege: {
        monthlyPV: Number;
        monthlyEvents: Number;
        collaborateUsers: Number;
        pageGroups: Number;
        conversions: Number;
        dataStorageMonths: Number;
        useUserIdentify: Boolean;
        useEventProperty: Boolean;
        useDataConnection: Boolean;
        useCrossDomain: Boolean;
        monthlyCampaignViewUsers: Number;
        campaigns: Number;
        userGroups: Number;
        usePopupForm: Boolean;
        useCampaignAbTest: Boolean;
        useCampaignUserGroupAndSession: Boolean;
        useCampaignCodeMode: Boolean;
        useCampaignJSTrigger: Boolean;
        useCampaignHeatmap: Boolean;
        useCampaignAI: Boolean;
        useHelpWebsite: Boolean;
        useCommunity: Boolean;
        useEmailSupport: Boolean;
        useLiveChatSupport: Boolean;
        usePhone: Boolean;
        useCSM: Boolean;
        useHttps: Boolean;
        useEventTracking: Boolean;
        usePopup: Boolean;
        useStickyBar: Boolean;
        useInline: Boolean;
        useCampaignReport: Boolean;
        useCampaignReportCSVDownload: Boolean;
        useCampaignSettingPage: Boolean;
        usePopupStickyBarTrigger: Boolean;
        useCampaignSchedule: Boolean;
        useCampaignGoal: Boolean;
    };
};

type PtiAllPrivilegeType = {
    privilege: {
        monthlyPV: Number;
        monthlyEvents: Number;
        collaborateUsers: Number;
        pageGroups: Number;
        conversions: Number;
        dataStorageMonths: Number;
        useUserIdentify: Boolean;
        useEventProperty: Boolean;
        useDataConnection: Boolean;
        useCrossDomain: Boolean;
        heatmaps: Number;
        HeatmapStorageMonths: Number;
        historicalHeatmaps: Number;
        useSegmentation: Boolean;
        useHeatmapCompetitor: Boolean;
        useSharableHeatmap: Boolean;
        useFunnel: Boolean;
        useHeatmapAbTest: Boolean;
        useHelpWebsite: Boolean;
        useCommunity: Boolean;
        useEmailSupport: Boolean;
        useLiveChatSupport: Boolean;
        usePhone: Boolean;
        useCSM: Boolean;
        useHttps: Boolean;
        useEventTracking: Boolean;
        useHeatmap: Boolean;
        useRealtimeTracking: Boolean;
        useDataInsightsReport: Boolean;
        usePageGroup: Boolean;
        useConversionTracking: Boolean;
        useCampaignManagement: Boolean;
        useBasicSegmentation: Boolean;
    };
};

type PackageInfo = {
    packageId: String;
    packageName: String;
    alias: String;
    isOnline: Boolean;
    area: String;
    originalPrice: {
        packageId: String;
        period: Number;
        monthlyOriginalPrice: Number;
        buyMode: String;
        area: String;
        taxRate: Number;
    };
    level: Number;
    currency: String;
    product: String;
    type: String;
};

export type PtiPackageInfo = PackageInfo & PtiAllPrivilegeType;
export type PtxPackageInfo = PackageInfo & PtxAllPrivilegeType;

type AllPackageInfo = {
    pti: Array<PtiPackageInfo>;
    ptx: Array<PtxPackageInfo>;
};

const { account } = useAccountInfo();
const allOnlinePackage = ref();

const freePackageInfo = computed(() => {
    if (!allOnlinePackage.value) return undefined;
    const experiencePackageInfo: PtxPackageInfo | undefined = allOnlinePackage.value?.ptx?.find(
        item => item.type?.toUpperCase() === 'FREE'
    );
    const insightPackageInfo: PtiPackageInfo | undefined = allOnlinePackage.value?.pti?.find(
        item => item.type?.toUpperCase() === 'FREE'
    );
    return {
        pti: insightPackageInfo,
        ptx: experiencePackageInfo
    };
});

export function useAllOnlinePackage() {
    const getAllOnlinePackage = function () {
        const { onResult, onError } = useQuery<{ getAllOnlinePackage: AllPackageInfo }>(
            QUERY_ALL_ONLINE_PACKAGE_SCHEMA,
            () => {
                return {
                    area: account.value?.area
                };
            }
        );

        onResult(data => {
            if (data.data.getAllOnlinePackage) {
                allOnlinePackage.value = data.data.getAllOnlinePackage;
            }
        });

        onError(() => {
            allOnlinePackage.value = undefined;
        });
    };

    return {
        freePackageInfo,
        allOnlinePackage,

        getAllOnlinePackage
    };
}
